import React, { FC, useMemo, useState } from "react";
import {
  useGetTasksExecutionQuery,
  useGetTasksAlarmStandAloneQuery,
  TaskExecution,
  TaskStandAloneAlarm,
} from "shared/api";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Button } from "antd";
import { format } from "date-fns";
import TaskGantt from "./TaskGantt";

export enum Statuses {
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

const statusColors: Record<string, string> = {
  FAILED: "bg-red-400",
  COMPLETED: "bg-green-500",
  PENDING: "bg-yellow-400",
  ACTIVE: "bg-green-500",
  INACTIVE: "bg-gray-400",
};

export type CalendarMode = "executions" | "standalone-alarms";

interface TaskCalendarProps {
  onBackClick?: () => void;
  mode?: CalendarMode;
}

const TaskCalendar: FC<TaskCalendarProps> = ({
  onBackClick,
  mode = "executions",
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<"schedule" | "gantt">("schedule");
  const [currentView, setCurrentView] = useState<
    "dayGridMonth" | "timeGridWeek" | "timeGridDay"
  >("dayGridMonth");

  const [range, setRange] = useState({ start: "", end: "" });

  // EXECUTIONS QUERY
  const executionArgs = {
    timestamp_expected__gte: range.start,
    timestamp_expected__lte: range.end,
    expand: "task",
  };

  const { data: executionTasks } = useGetTasksExecutionQuery(executionArgs, {
    skip: mode !== "executions" || !range.start || !range.end,
    refetchOnMountOrArgChange: true,
  });

  // STANDALONE ALARMS QUERY
  // Use overlap logic: alarm overlaps view if triggered <= range.end AND updated >= range.start
  const alarmArgs = {
    timestamp_triggered__lte: range.end,
    timestamp_updated__gte: range.start,
    expand: "task_standalone",
  };

  const { data: alarmTasks } = useGetTasksAlarmStandAloneQuery(alarmArgs, {
    skip: mode !== "standalone-alarms" || !range.start || !range.end,
    refetchOnMountOrArgChange: true,
  });

  const options = useMemo(
    () => [
      {
        label: t("taskHub.schedule.schedule") || "Schedule",
        value: "schedule",
      },
      {
        label: t("taskHub.schedule.ganttView") || "Gantt View",
        value: "gantt",
      },
    ],
    [t]
  );

  const getEventColor = (status: Statuses): { bg: string; text: string } => {
    if (status === Statuses.PENDING) {
      return { bg: statusColors[status], text: "text-black" };
    }
    return { bg: statusColors[status] ?? "bg-gray-400", text: "text-white" };
  };

  const calendarEvents = useMemo(() => {
    if (mode === "executions") {
      return (
        executionTasks?.map((task: TaskExecution, i: number) => {
          const start = new Date(task.timestamp_expected);
          start.setHours(8 + Math.floor(i % 10));
          start.setMinutes((i * 10) % 60);
          const end = new Date(start);
          end.setHours(start.getHours() + 1);

          return {
            id: String(task.id),
            title:
              task.task?.name ??
              (t("taskHub.schedule.unnamedTask") as string) ??
              "Unnamed Task",
            start: start.toISOString(),
            end: end.toISOString(),
            extendedProps: {
              status: task.status as Statuses,
            },
          };
        }) ?? []
      );
    }

    return (
      alarmTasks?.map((alarm: TaskStandAloneAlarm) => {
        const start = new Date(alarm.timestamp_triggered);
        const end = new Date(alarm.timestamp_updated);

        return {
          id: String(alarm.id),
          title: alarm.task_standalone?.name ?? "Standalone Alarm",
          start: start.toISOString(),
          end: end.toISOString(),
          extendedProps: {
            status: alarm.is_active ? Statuses.ACTIVE : Statuses.INACTIVE,
          },
        };
      }) ?? []
    );
  }, [mode, executionTasks, alarmTasks, t]);

  return (
    <div className="w-screen flex flex-col bg-white text-black overflow-hidden">
      <div className="px-6 pt-6 flex items-center gap-3">
        <Button
          onClick={onBackClick}
          type="primary"
          className="btn-primary--dark"
        >
          {t("taskHub.schedule.goBack") || "Go back"}
        </Button>

        <h1 className="text-4xl font-bold mb-2">
          {t("taskHub.schedule.taskHubSchedule") || "Task Hub Schedule"}
        </h1>

        <div className="ml-auto flex bg-slate-800 rounded-full overflow-hidden shadow-sm w-fit">
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => setViewMode(option.value as "schedule" | "gantt")}
              className={`px-4 py-1 text-sm font-semibold transition-colors duration-200 ${
                viewMode === option.value
                  ? "bg-slate-700 text-white"
                  : "text-slate-300 hover:bg-slate-700/50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {viewMode === "schedule" ? (
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            firstDay={1}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "timeGridDay timeGridWeek dayGridMonth",
            }}
            buttonText={{
              today: t("taskHub.schedule.today") || "Today",
              day: t("taskHub.schedule.day") || "Day",
              week: t("taskHub.schedule.week") || "Week",
              month: t("taskHub.schedule.month") || "Month",
            }}
            events={calendarEvents}
            dayMaxEvents={4}
            height="100vh"
            viewDidMount={(arg) => {
              const view = arg.view.type as typeof currentView;
              setCurrentView(view);
              setRange({
                start: format(arg.view.currentStart, "yyyy-MM-dd'T'00:00:00"),
                end: format(arg.view.currentEnd, "yyyy-MM-dd'T'23:59:59"),
              });
            }}
            datesSet={(arg) => {
              const view = arg.view.type as typeof currentView;
              setCurrentView(view);
              setRange({
                start: format(arg.start, "yyyy-MM-dd'T'00:00:00"),
                end: format(arg.end, "yyyy-MM-dd'T'23:59:59"),
              });
            }}
            eventClassNames={(arg) => {
              const { status } = arg.event.extendedProps as {
                status: Statuses;
              };
              const { bg, text } = getEventColor(status);
              return [
                "rounded-md px-2 py-1 text-xs font-semibold shadow-sm",
                bg,
                text,
              ];
            }}
            eventContent={(arg) => {
              const { start, end, extendedProps } = arg.event;
              const { status } = extendedProps as { status: Statuses };

              const timeRange =
                start && end
                  ? `${format(start, "yyyy-MM-dd HH:mm")} - ${format(
                      end,
                      "yyyy-MM-dd HH:mm"
                    )}`
                  : "";

              const statusTextColor =
                status === Statuses.PENDING ? "text-black" : "text-white";

              return (
                <div
                  className="flex flex-col w-full h-full cursor-pointer"
                  title={`${arg.event.title} (${timeRange})`}
                >
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-white opacity-80" />
                    <span className={statusTextColor}>{arg.event.title}</span>
                  </div>
                  <div
                    className={`text-[10px] opacity-90 pl-4 leading-tight ${statusTextColor}`}
                  >
                    {String(status)}
                  </div>
                </div>
              );
            }}
          />
        ) : (
          <TaskGantt events={calendarEvents} />
        )}
      </div>
    </div>
  );
};

export default TaskCalendar;
